import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMapEvents, useMap, Polygon, Polyline, CircleMarker } from 'react-leaflet';
import CsvImport from '../components/app/CsvImport';
import SiteMasterModal from '../components/app/SiteMasterModal';
import L from 'leaflet';
import JSZip from 'jszip';
import { Upload, MapPin, Layers, X, Search, Save, Building, Trash2, Sparkles, Radio, FileSearch, Loader2, ExternalLink, BarChart3, Zap, Eye, Kanban, ShieldAlert, Landmark, Pencil, CheckCircle, Undo2 } from 'lucide-react';
import AreaAnalysisPanel from '../components/app/AreaAnalysisPanel';
import NodesLayer from '../components/app/prospeccion/NodesLayer';
import TowersLayer from '../components/app/prospeccion/TowersLayer';
import { LayersToggle, OperatorFilter } from '../components/app/prospeccion/MapToolbar';
import { towerIcon, protectedIcon, coverageStyle as CoverageStyle, streetViewUrl, googleMapsUrl } from '../components/app/prospeccion/mapAssets';
import ProspeccionBoard from '../components/app/prospeccion/ProspeccionBoard';

// Fix Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Parse KML/KMZ to GeoJSON (simple polygon/point extractor using DOMParser)
async function parseKmzFile(file) {
  let kmlText;
  if (file.name.toLowerCase().endsWith('.kmz')) {
    const zip = await JSZip.loadAsync(file);
    const kmlFile = Object.keys(zip.files).find((f) => f.toLowerCase().endsWith('.kml'));
    if (!kmlFile) throw new Error('No .kml dentro del KMZ');
    kmlText = await zip.files[kmlFile].async('text');
  } else {
    kmlText = await file.text();
  }
  return kmlToGeoJSON(kmlText);
}

function kmlToGeoJSON(kmlText) {
  const parser = new DOMParser();
  const xml = parser.parseFromString(kmlText, 'text/xml');
  const features = [];
  const placemarks = xml.getElementsByTagName('Placemark');
  for (let i = 0; i < placemarks.length; i++) {
    const pm = placemarks[i];
    const name = pm.getElementsByTagName('name')[0]?.textContent || `Area ${i+1}`;
    // Polygon
    const polys = pm.getElementsByTagName('Polygon');
    for (let j = 0; j < polys.length; j++) {
      const coordsEl = polys[j].getElementsByTagName('coordinates')[0];
      if (!coordsEl) continue;
      const coordsText = coordsEl.textContent.trim();
      const ring = coordsText.split(/\s+/).map((t) => {
        const [lng, lat] = t.split(',').map(Number);
        return [lng, lat];
      }).filter((c) => !isNaN(c[0]) && !isNaN(c[1]));
      features.push({ type: 'Feature', properties: { name }, geometry: { type: 'Polygon', coordinates: [ring] } });
    }
    // Points
    const points = pm.getElementsByTagName('Point');
    for (let j = 0; j < points.length; j++) {
      const coordsEl = points[j].getElementsByTagName('coordinates')[0];
      if (!coordsEl) continue;
      const [lng, lat] = coordsEl.textContent.trim().split(',').map(Number);
      features.push({ type: 'Feature', properties: { name }, geometry: { type: 'Point', coordinates: [lng, lat] } });
    }
    // LineString
    const lines = pm.getElementsByTagName('LineString');
    for (let j = 0; j < lines.length; j++) {
      const coordsEl = lines[j].getElementsByTagName('coordinates')[0];
      if (!coordsEl) continue;
      const coords = coordsEl.textContent.trim().split(/\s+/).map((t) => {
        const [lng, lat] = t.split(',').map(Number);
        return [lng, lat];
      });
      features.push({ type: 'Feature', properties: { name }, geometry: { type: 'LineString', coordinates: coords } });
    }
  }
  return { type: 'FeatureCollection', features };
}

// Estimate building height in meters from OSM building tags around a point
async function estimateBuildingHeight(lat, lng) {
  // Overpass API query for buildings within 50m
  const q = `[out:json][timeout:15];(way["building"](around:50,${lat},${lng});relation["building"](around:50,${lat},${lng}););out tags;`;
  try {
    const r = await fetch('https://overpass-api.de/api/interpreter', {
      method: 'POST', body: q,
    });
    const data = await r.json();
    const heights = [];
    for (const el of data.elements || []) {
      const t = el.tags || {};
      if (t.height) {
        const h = parseFloat(String(t.height).replace(',', '.'));
        if (!isNaN(h)) heights.push(h);
      } else if (t['building:levels']) {
        const lvl = parseFloat(t['building:levels']);
        if (!isNaN(lvl)) heights.push(lvl * 3); // 3m per level estimation
      }
    }
    if (heights.length === 0) return null;
    const avg = heights.reduce((a,b)=>a+b,0) / heights.length;
    return { avg: Math.round(avg*10)/10, count: heights.length, max: Math.max(...heights) };
  } catch (e) { console.error(e); return null; }
}

async function reverseGeocode(lat, lng) {
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`, {
      headers: { 'Accept-Language': 'es' }
    });
    const data = await r.json();
    return data;
  } catch (e) { return null; }
}

const FlyTo = ({ pos }) => {
  const map = useMap();
  useEffect(() => { if (pos) map.flyTo(pos, 17, { duration: 1 }); }, [pos]);
  return null;
};

const ClickHandler = ({ onClick }) => {
  useMapEvents({ click: (e) => onClick([e.latlng.lat, e.latlng.lng]) });
  return null;
};

// Drawing tool: user clicks to add vertices; double-click finishes.
// Coords are stored as [lat, lng] pairs (react-leaflet convention).
const AreaDrawer = ({ enabled, points, onAddPoint, onFinish }) => {
  useMapEvents({
    click: (e) => {
      if (!enabled) return;
      onAddPoint([e.latlng.lat, e.latlng.lng]);
    },
    dblclick: (e) => {
      if (!enabled) return;
      onFinish();
    },
  });
  if (!enabled || points.length === 0) return null;
  return (
    <>
      {points.length >= 3 ? (
        <Polygon positions={points} pathOptions={{ color: '#A3FF12', fillColor: '#A3FF12', fillOpacity: 0.15, weight: 2, dashArray: '6 6' }}/>
      ) : (
        <Polyline positions={points} pathOptions={{ color: '#A3FF12', weight: 2, dashArray: '6 6' }}/>
      )}
      {points.map((p, i) => (
        <CircleMarker key={i} center={p} radius={5} pathOptions={{ color: '#A3FF12', fillColor: '#000', fillOpacity: 1, weight: 2 }}/>
      ))}
    </>
  );
};

// Utility: extract lat/lng bounds from any GeoJSON FeatureCollection
const geojsonBounds = (geojson) => {
  if (!geojson?.features?.length) return null;
  const pts = [];
  const walk = (coords) => {
    if (typeof coords[0] === 'number') { pts.push([coords[1], coords[0]]); return; }
    coords.forEach(walk);
  };
  geojson.features.forEach((f) => f?.geometry?.coordinates && walk(f.geometry.coordinates));
  if (pts.length === 0) return null;
  const lats = pts.map((p) => p[0]);
  const lngs = pts.map((p) => p[1]);
  return [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]];
};

// Centra + hace zoom al área focused (cuando cambia el id)
const FitBoundsToArea = ({ areaId, kmzAreas }) => {
  const map = useMap();
  useEffect(() => {
    if (!areaId) return;
    const a = kmzAreas.find((x) => x.id === areaId);
    if (!a) return;
    const b = geojsonBounds(a.geojson);
    if (b) map.fitBounds(b, { padding: [40, 40], maxZoom: 17, animate: true });
  }, [areaId, kmzAreas, map]);
  return null;
};

// Fetch active nodes on map move (debounced, stable ref)
const NodesFetcher = ({ enabled, onBoundsChange }) => {
  const map = useMap();
  const timer = useRef(null);
  const trigger = useCallback(() => {
    if (!enabled) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const b = map.getBounds();
      onBoundsChange({ south: b.getSouth(), north: b.getNorth(), west: b.getWest(), east: b.getEast() });
    }, 300);
  }, [enabled, map, onBoundsChange]);

  useMapEvents({
    moveend: trigger,
    zoomend: trigger,
  });
  useEffect(() => {
    trigger();
    return () => clearTimeout(timer.current);
  }, [trigger]);
  return null;
};

// Catastro español - reverse lookup by coordinates (called from browser to bypass server IP restrictions)
async function fetchCatastro(lat, lng) {
  try {
    // Use proxy via OpenAtlas / public CORS-friendly mirror, fallback to Catastro WMS
    const url = `https://ovc.catastro.meh.es/ovcservweb/OVCSWLocalizacionRC/OVCCoordenadas.asmx/Consulta_RCCOOR?SRS=EPSG:4326&Coordenada_X=${lng}&Coordenada_Y=${lat}`;
    const r = await fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`);
    const text = await r.text();
    const rc1 = text.match(/<pc1>([^<]+)<\/pc1>/);
    const rc2 = text.match(/<pc2>([^<]+)<\/pc2>/);
    const ldt = text.match(/<ldt>([^<]+)<\/ldt>/);
    const reference = rc1 && rc2 ? rc1[1] + rc2[1] : null;
    if (!reference) return null;
    // Get details
    let details = {};
    try {
      const dUrl = `https://ovc.catastro.meh.es/ovcservweb/OVCSWLocalizacionRC/OVCCallejero.asmx/Consulta_DNPRC?RC=${reference}`;
      const r2 = await fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(dUrl)}`);
      const dtxt = await r2.text();
      const sup = dtxt.match(/<sfc>(\d+)<\/sfc>/);
      if (sup) details.surface_m2 = parseInt(sup[1]);
      const ano = dtxt.match(/<ant>(\d+)<\/ant>/);
      if (ano) details.year_built = parseInt(ano[1]);
      const uso = dtxt.match(/<luso>([^<]+)<\/luso>/);
      if (uso) details.use = uso[1].trim();
    } catch { /* details are best-effort */ }
    return { reference, address: ldt?.[1], ...details };
  } catch (e) {
    return null;
  }
}

// Icons + URL helpers now live in components/app/prospeccion/mapAssets.js

const Prospeccion = () => {
  const [kmzAreas, setKmzAreas] = useState([]);
  const [users, setUsers] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [normativa, setNormativa] = useState([]);
  const [point, setPoint] = useState(null);
  const [info, setInfo] = useState(null);
  const [matchedNorm, setMatchedNorm] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [search, setSearch] = useState('');
  const [center, setCenter] = useState([40.4168, -3.7038]); // Madrid
  const [saving, setSaving] = useState(false);
  const [catastro, setCatastro] = useState(null);
  const [towers, setTowers] = useState([]);
  const [coverageInfo, setCoverageInfo] = useState(null);
  const [researching, setResearching] = useState(false);
  const [aiNorm, setAiNorm] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisAreaName, setAnalysisAreaName] = useState('');
  const [electricity, setElectricity] = useState(null);
  const [heritage, setHeritage] = useState(null);
  const [showNodes, setShowNodes] = useState(true);
  const [showTotemTowers, setShowTotemTowers] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [totemTowers, setTotemTowers] = useState([]);
  const [nodesLoading, setNodesLoading] = useState(false);
  const [nodeStats, setNodeStats] = useState(null);
  const [towersStats, setTowersStats] = useState(null);
  const [opFilter, setOpFilter] = useState(null); // null = all
  const [statusFilter, setStatusFilter] = useState(null); // null | 'sin_visitar' | 'activos' | 'conflictivos'
  const [view, setView] = useState('map'); // 'map' | 'board'
  const [activityStatus, setActivityStatus] = useState({ active: new Set(), conflict: new Set(), counts: { active: 0, conflict: 0 } });
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [detailCode, setDetailCode] = useState(null); // {code, lat, lng} to open SiteMasterModal
  const [kmzToast, setKmzToast] = useState(null); // {msg, type: 'ok'|'warn'|'err'}
  const [drawMode, setDrawMode] = useState(false);
  const [drawPoints, setDrawPoints] = useState([]); // [[lat,lng], ...]
  const [focusedAreaId, setFocusedAreaId] = useState(null);
  // Tipos de elementos a buscar. Los 3 marcados por defecto son los del caso principal.
  const [analysisTypes, setAnalysisTypes] = useState({
    buildings: true, towers: true, heritage: true,
    shops: false, hotels: false, organisms: false,
    education: false, health: false, religion: false, sports: false,
  });
  const fileRef = useRef();
  const [searchParams] = useSearchParams();

  // Deep-link from GlobalSearch: /app/prospeccion?lat=..&lng=..&code=..&focus=..
  useEffect(() => {
    const lat = parseFloat(searchParams.get('lat'));
    const lng = parseFloat(searchParams.get('lng'));
    const code = searchParams.get('code');
    if (!isNaN(lat) && !isNaN(lng)) {
      setCenter([lat, lng]);
      if (code) {
        // Imported KMZ/maestro site → open the 58-field modal
        setDetailCode({ code, lat, lng });
      } else {
        // Prospection-created site: drop the pin so the side panel shows
        setPoint([lat, lng]);
      }
    }
  }, [searchParams]);

  const load = async () => {
    const [k, c, n, ns, ts, ast] = await Promise.all([
      api.get('/kmz'),
      api.get('/coverage-zones'),
      api.get('/normativa'),
      api.get('/nodes/stats').catch(() => ({ data: null })),
      api.get('/towers/stats').catch(() => ({ data: null })),
      api.get('/nodes/activity-status').catch(() => ({ data: null })),
    ]);
    setKmzAreas(k.data);
    setCoverage(c.data);
    setNormativa(n.data);
    if (ns.data) setNodeStats(ns.data);
    if (ts.data) setTowersStats(ts.data);
    if (ast.data) setActivityStatus({
      active:   new Set(ast.data.active_codes || []),
      conflict: new Set(ast.data.conflict_codes || []),
      counts:   ast.data.counts || { active: 0, conflict: 0 },
    });
  };
  useEffect(() => { load(); }, []);

  // fetch active nodes when bbox changes (throttled by leaflet moveend)
  const fetchNodesInBbox = useCallback(async (b) => {
    if (!showNodes && !showTotemTowers) return;
    setNodesLoading(true);
    try {
      const bboxParams = {
        min_lat: b.south, max_lat: b.north, min_lng: b.west, max_lng: b.east, limit: 2000,
      };
      const promises = [];
      if (showNodes) {
        const p = { ...bboxParams };
        if (opFilter) p.operator = opFilter;
        promises.push(api.get('/nodes', { params: p }).then((r) => setNodes(r.data)).catch((e) => console.error(e)));
      } else { setNodes([]); }
      if (showTotemTowers) {
        promises.push(api.get('/towers', { params: bboxParams }).then((r) => setTotemTowers(r.data)).catch((e) => console.error(e)));
      } else { setTotemTowers([]); }
      await Promise.all(promises);
    } finally { setNodesLoading(false); }
  }, [showNodes, showTotemTowers, opFilter]);

  // Apply status filter (client-side) — check if a node/tower's code is in the active/conflict sets.
  const matchesStatus = useCallback((code) => {
    if (!statusFilter) return true;
    const c = code || '';
    if (statusFilter === 'activos') return activityStatus.active.has(c);
    if (statusFilter === 'conflictivos') return activityStatus.conflict.has(c);
    if (statusFilter === 'sin_visitar') return !activityStatus.active.has(c) && !activityStatus.conflict.has(c);
    return true;
  }, [statusFilter, activityStatus]);

  const nodesFiltered = React.useMemo(() => {
    if (!statusFilter) return nodes;
    return nodes.filter((n) => matchesStatus(n.linked_master_code || n.code || n.alt_code));
  }, [nodes, statusFilter, matchesStatus]);
  const towersFiltered = React.useMemo(() => {
    if (!statusFilter) return totemTowers;
    return totemTowers.filter((t) => matchesStatus(t.linked_master_code || t.code));
  }, [totemTowers, statusFilter, matchesStatus]);

  // search sites (nodes + towers)
  useEffect(() => {
    if (!searchQ || searchQ.length < 2) { setSearchResults([]); return; }
    let cancelled = false;
    setSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/nodes/search', { params: { q: searchQ, limit: 15 } });
        if (!cancelled) setSearchResults(data);
      } catch { /* ignore */ }
      finally { if (!cancelled) setSearchLoading(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchQ]);

  const gotoResult = (r) => {
    setCenter([r.lat, r.lng]);
    setSearchOpen(false);
    setSearchQ('');
    setSearchResults([]);
    if (r.code) setDetailCode({ code: r.code, lat: r.lat, lng: r.lng });
  };

  const analyzeArea = async (geojson, name) => {
    // Find the first polygon and extract coordinates
    const poly = geojson.features.find((f) => f.geometry?.type === 'Polygon');
    if (!poly) { alert('No se encontró un polígono en el KMZ. Asegúrate de incluir un área cerrada.'); return; }
    const coords = poly.geometry.coordinates[0]; // outer ring [lng, lat]

    // Validación tamaño: evita bloquear el backend con áreas de decenas de km²
    // (centro Madrid entero → decenas de miles de edificios en OSM → 502 en Cloudflare)
    const lats = coords.map((c) => c[1]);
    const lngs = coords.map((c) => c[0]);
    const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const hKm = (Math.max(...lats) - Math.min(...lats)) * 111.0;
    const wKm = (Math.max(...lngs) - Math.min(...lngs)) * 111.0 * Math.cos(midLat * Math.PI / 180);
    const bboxKm2 = Math.round(hKm * wKm * 100) / 100;
    if (bboxKm2 > 25.0) {
      setKmzToast({
        type: 'warn',
        msg: `Área demasiado grande (${bboxKm2} km²). Máximo 25 km². Divide el polígono en zonas más pequeñas y vuelve a intentarlo.`,
      });
      // No cerramos el toast, que se quede visible hasta que el user lo cierre
      return;
    }

    setAnalyzing(true);
    setAnalysisAreaName(name);
    const types = Object.entries(analysisTypes).filter(([, v]) => v).map(([k]) => k);
    // Retry loop: si backend devuelve 202 (job en curso) reintentamos hasta 3 min.
    const maxAttempts = 24;  // 24 * 8s = 192s ≈ 3 min
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const r = await api.post('/research/area-analysis', { coords, types });
        setAnalysis(r.data);
        setAnalyzing(false);
        return;
      } catch (e) {
        const status = e?.response?.status;
        if (status === 202) {
          // Job en curso — esperamos 8s y reintentamos silenciosamente
          setKmzToast({
            type: 'ok',
            msg: `Analizando el área en background (Overpass tarda en frío)… reintentando en 8s (${attempt + 1}/${maxAttempts})`,
          });
          await new Promise((r) => setTimeout(r, 8000));
          continue;
        }
        // Error real
        const detail = e?.response?.data?.detail || e.message;
        setKmzToast({ type: 'err', msg: `Error al analizar área: ${detail}` });
        setAnalyzing(false);
        return;
      }
    }
    setKmzToast({ type: 'err', msg: 'El análisis está tardando demasiado. Vuelve a intentarlo — el resultado quedará en caché.' });
    setAnalyzing(false);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const geojson = await parseKmzFile(file);
      if (!geojson.features.length) { alert('No se encontraron geometrías en el archivo.'); return; }
      // Cuenta por tipo para dar feedback claro al usuario
      const counts = geojson.features.reduce((acc, f) => {
        const t = f.geometry?.type || 'Unknown';
        acc[t] = (acc[t] || 0) + 1;
        return acc;
      }, {});
      const polys = counts.Polygon || 0;
      const points = counts.Point || 0;
      const lines = counts.LineString || 0;
      await api.post('/kmz', { name: file.name, geojson });
      await load();
      // Center map on first feature
      const first = geojson.features[0];
      if (first.geometry.type === 'Polygon') {
        const c = first.geometry.coordinates[0][0];
        setCenter([c[1], c[0]]);
      } else if (first.geometry.type === 'Point') {
        setCenter([first.geometry.coordinates[1], first.geometry.coordinates[0]]);
      } else if (first.geometry.type === 'LineString' && first.geometry.coordinates.length) {
        const c = first.geometry.coordinates[0];
        setCenter([c[1], c[0]]);
      }
      // Notificación con recuento
      const parts = [];
      if (polys)  parts.push(`${polys} área${polys !== 1 ? 's' : ''}`);
      if (points) parts.push(`${points} punto${points !== 1 ? 's' : ''}`);
      if (lines)  parts.push(`${lines} línea${lines !== 1 ? 's' : ''}`);
      const summary = `KMZ importado: ${parts.join(' · ')}`;
      // Reset input para que se pueda re-subir el mismo archivo
      if (e.target) e.target.value = '';
      // Si hay polígonos, arranca análisis automático del primero
      if (polys > 0) {
        setKmzToast({ msg: `${summary}. Analizando área…`, type: 'ok' });
        analyzeArea(geojson, file.name);
      } else {
        setKmzToast({ msg: `${summary}. Solo se puede "Prospectar en el área" si tiene polígonos cerrados.`, type: 'warn' });
      }
      setTimeout(() => setKmzToast(null), 6000);
    } catch (err) {
      alert('Error al parsear: ' + err.message);
      if (e.target) e.target.value = '';
    }
  };

  const reanalyze = (kmz) => analyzeArea(kmz.geojson, kmz.name);

  // ---- Drawing tool handlers ----
  const startDraw = () => { setDrawMode(true); setDrawPoints([]); setKmzToast({ type: 'ok', msg: 'Haz clic en el mapa para añadir vértices. Doble clic para cerrar el área.' }); };
  const cancelDraw = () => { setDrawMode(false); setDrawPoints([]); setKmzToast(null); };
  const addDrawPoint = (latlng) => setDrawPoints((prev) => [...prev, latlng]);
  const undoLastPoint = () => setDrawPoints((prev) => prev.slice(0, -1));

  const finishDraw = async (nameOverride) => {
    if (drawPoints.length < 3) {
      setKmzToast({ type: 'warn', msg: 'Necesitas al menos 3 vértices para cerrar el área.' });
      return;
    }
    const name = nameOverride || window.prompt('Nombre para esta área:', `Área dibujada ${new Date().toLocaleString('es-ES')}`);
    if (!name) return;

    // Convert [lat,lng] → GeoJSON polygon [[lng,lat], ...] closed ring
    const ring = drawPoints.map(([lat, lng]) => [lng, lat]);
    // Close the ring if not already closed
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first);
    const geojson = {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: { name }, geometry: { type: 'Polygon', coordinates: [ring] } }],
    };

    try {
      await api.post('/kmz', { name, geojson });
      setDrawMode(false);
      setDrawPoints([]);
      await load();
      setKmzToast({ type: 'ok', msg: `Área "${name}" guardada. Analizando…` });
      analyzeArea(geojson, name);
      setTimeout(() => setKmzToast(null), 5000);
    } catch (e) {
      setKmzToast({ type: 'err', msg: 'Error al guardar el área: ' + (e?.response?.data?.detail || e.message) });
    }
  };

  const saveCandidate = async (cand, afeccionesReport) => {
    try {
      const muni = analysisAreaName || '';
      const payload = {
        name: cand.name || `Edificio ${cand.height_m}m`,
        address: cand.address ? `${cand.address}${cand.housenumber ? ' '+cand.housenumber : ''}` : '',
        lat: cand.lat, lng: cand.lng,
        building_height_m: cand.height_m || null,
        type: 'urbano',
        municipality: muni,
        status: 'prospeccion',
        priority: 'L3',
        coverage_quality: coverageInfo?.quality || 'media',
        notes: `Candidato detectado en KMZ "${muni}" · ${cand.type || ''} · ${cand.levels ? cand.levels+' plantas' : ''}`,
      };
      // Adjunta el informe de afecciones si el usuario ya lo analizó — evita re-analizar
      if (afeccionesReport && Array.isArray(afeccionesReport.results)) {
        payload.afecciones_report = afeccionesReport;
        payload.afecciones_generated_at = afeccionesReport.generated_at;
      }
      await api.post('/sites', payload);
      // light feedback without closing the modal
    } catch (e) {
      alert('Error al guardar: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const onMapClick = async ([lat, lng]) => {
    setPoint([lat, lng]);
    setLoadingInfo(true);
    setInfo(null); setMatchedNorm(null); setCatastro(null); setTowers([]); setCoverageInfo(null); setAiNorm(null); setElectricity(null); setHeritage(null);
    const [addr, h] = await Promise.all([reverseGeocode(lat, lng), estimateBuildingHeight(lat, lng)]);
    setInfo({ address: addr, height: h, lat, lng });

    // Match normativa by municipality (from local DB)
    const muni = addr?.address?.city || addr?.address?.town || addr?.address?.village || addr?.address?.municipality;
    if (muni) {
      const match = normativa.find((nn) => nn.municipality.toLowerCase() === muni.toLowerCase());
      if (match) setMatchedNorm(match);
    }
    setLoadingInfo(false);

    // Kick off Catastro lookup + towers in background (don't block UI)
    fetchCatastro(lat, lng).then((c) => setCatastro(c));
    api.get('/research/cell-towers', { params: { lat, lng, radius_m: 2000 } })
      .then((r) => { setTowers(r.data.towers || []); setCoverageInfo({ count: r.data.tower_count, quality: r.data.coverage_quality }); })
      .catch(() => {});
    // Rural electricity infrastructure (always query; useful for both urban and rural)
    api.get('/research/electricity', { params: { lat, lng, radius_m: 1500 } })
      .then((r) => setElectricity(r.data))
      .catch(() => {});
    // Catalogación del edificio (BIC / patrimonio / catálogo).
    // Radio de búsqueda: cuando la dirección incluye número exacto ampliamos
    // (60m) porque el centroide del polígono de un edificio grande puede estar
    // a >30m de la fachada geocodificada. Sin número usamos 40m.
    const hasHouseNumber = !!addr?.address?.house_number;
    const heritageRadius = hasHouseNumber ? 60 : 40;
    api.get('/research/building-heritage', { params: { lat, lng, radius_m: heritageRadius } })
      .then((r) => setHeritage(r.data))
      .catch(() => {});
  };

  const researchNormativaAI = async () => {
    if (!info?.address) return;
    const muni = info.address.address?.city || info.address.address?.town || info.address.address?.village || info.address.address?.municipality;
    const province = info.address.address?.province || info.address.address?.state || '';
    if (!muni) { alert('No se ha podido determinar el municipio.'); return; }
    setResearching(true);
    try {
      // 1) Fetch the AI legal dossier first. Rendering the card must NOT be
      //    gated on the affections request — both are slow LLM calls and
      //    running them in parallel doubles the ingress-timeout risk.
      const rn = await api.post('/research/normativa', {
        municipality: muni,
        province,
        type: 'urbano',
        lat: info.lat,
        lng: info.lng,
        address: info.address?.display_name || '',
      });
      setAiNorm(rn.data);

      // 2) Fire-and-forget: fetch the 10 territorial affections in the
      //    background and merge them into the dossier state when ready.
      api.post('/afecciones/analyze', {
        lat: info.lat,
        lng: info.lng,
        address: info.address?.display_name || '',
      }).then((ra) => {
        if (ra?.data?.results) {
          setAiNorm((prev) => prev ? {
            ...prev,
            afecciones: ra.data.results,
            afecciones_summary: ra.data.summary || '',
          } : prev);
        }
      }).catch(() => {
        // Hide the "loading" spinner if the affections request fails
        setAiNorm((prev) => prev ? { ...prev, afecciones_error: true } : prev);
      });
    } catch (e) {
      alert('Error al investigar: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setResearching(false);
    }
  };

  const saveAiNormToDb = async () => {
    if (!aiNorm) return;
    try {
      await api.post('/normativa', {
        municipality: aiNorm.municipality,
        province: info?.address?.address?.province || '',
        type: aiNorm.type,
        max_height_m: aiNorm.max_height_m,
        min_distance_residential_m: aiNorm.min_distance_residential_m,
        retranqueo_m: aiNorm.retranqueo_m,
        extra_rules: aiNorm.extra_rules || '',
        license_regime: aiNorm.license_regime || '',
        license_conditions: aiNorm.license_conditions || '',
        pgou_reference: aiNorm.pgou_reference || '',
        pgou_url: aiNorm.pgou_url || '',
        protected_zones: aiNorm.protected_zones || '',
        state_framework: aiNorm.state_framework || '',
        autonomic_framework: aiNorm.autonomic_framework || '',
        has_local_ordinance: aiNorm.has_local_ordinance,
        requires_license: aiNorm.requires_license,
        source_url: aiNorm.source_url || '',
        notes: aiNorm.notes,
        verified: false,
      });
      alert('Normativa guardada en la base de datos (sin verificar).');
      load();
    } catch (e) {
      alert('Error al guardar: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const doSearch = async (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(search)}&countrycodes=es&limit=1`);
    const d = await r.json();
    if (d[0]) {
      const lat = parseFloat(d[0].lat), lng = parseFloat(d[0].lon);
      setCenter([lat, lng]);
      onMapClick([lat, lng]);
    }
  };

  const saveAsSite = async () => {
    if (!info) return;
    setSaving(true);
    try {
      const muni = info.address?.address?.city || info.address?.address?.town || info.address?.address?.village || '';
      const province = info.address?.address?.province || info.address?.address?.state || '';
      await api.post('/sites', {
        name: info.address?.display_name?.split(',')[0] || `Emplazamiento ${Date.now()}`,
        address: info.address?.display_name || '',
        lat: info.lat, lng: info.lng,
        building_height_m: info.height?.avg || null,
        type: 'urbano',
        municipality: muni,
        province,
        status: 'prospeccion',
        priority: 'L3',
        coverage_quality: coverageInfo?.quality || 'media',
      });
      alert(`Emplazamiento guardado · Cobertura: ${coverageInfo?.quality?.toUpperCase() || 'MEDIA'}`);
    } catch (e) { alert('Error: ' + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const deleteKmz = async (id) => {
    if (!confirm('¿Eliminar esta capa KMZ?')) return;
    await api.delete(`/kmz/${id}`);
    load();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6 lg:mb-8">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12] mb-2">PROSPECCIÓN</div>
          <h1 className="text-3xl lg:text-4xl font-black text-white">Mapa de emplazamientos</h1>
          <p className="text-white/50 mt-2 text-sm lg:text-base">Sube un KMZ, busca direcciones, mira altura media y normativa local.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex bg-black/40 border border-white/10 rounded-full p-1">
            <button onClick={() => setView('map')} data-testid="view-toggle-map"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                view === 'map' ? 'bg-[#A3FF12] text-black' : 'text-white/60 hover:text-white'
              }`}>
              <MapPin size={13}/> Mapa
            </button>
            <button onClick={() => setView('board')} data-testid="view-toggle-board"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                view === 'board' ? 'bg-[#A3FF12] text-black' : 'text-white/60 hover:text-white'
              }`}>
              <Kanban size={13}/> Kanban
            </button>
          </div>
          <LayersToggle
            showNodes={showNodes} setShowNodes={setShowNodes} nodeStats={nodeStats}
            showTotemTowers={showTotemTowers} setShowTotemTowers={setShowTotemTowers} towersStats={towersStats}
            nodesLoading={nodesLoading}
            fileRef={fileRef} onFile={onFile} onImported={load}
          />
          <button onClick={() => fileRef.current?.click()} data-testid="btn-import-kmz-areas"
            className="inline-flex items-center gap-2 bg-[#A3FF12] hover:bg-[#b8ff3a] text-black font-black px-4 py-2 rounded-full text-xs">
            <Upload size={13}/> Importar área KMZ
          </button>
          <button onClick={drawMode ? cancelDraw : startDraw} data-testid="btn-draw-area"
            className={`inline-flex items-center gap-2 font-black px-4 py-2 rounded-full text-xs border ${
              drawMode
                ? 'bg-red-500/15 border-red-400/60 text-red-200'
                : 'bg-white/5 border-white/15 text-white/80 hover:bg-white/10'
            }`}>
            <Pencil size={13}/> {drawMode ? 'Cancelar dibujo' : 'Dibujar área'}
          </button>
        </div>
      </div>

      {kmzToast && (
        <div data-testid="kmz-toast"
          className={`mb-4 rounded-xl px-4 py-3 text-sm border flex items-center gap-2 ${
            kmzToast.type === 'ok'   ? 'bg-[#A3FF12]/10 border-[#A3FF12]/30 text-[#A3FF12]' :
            kmzToast.type === 'warn' ? 'bg-orange-500/10 border-orange-500/30 text-orange-300' :
                                       'bg-red-500/10 border-red-500/30 text-red-300'
          }`}>
          <Layers size={14}/> {kmzToast.msg}
          <button onClick={() => setKmzToast(null)} className="ml-auto text-white/60 hover:text-white text-xs">✕</button>
        </div>
      )}

      {view === 'board' ? (
        <ProspeccionBoard onOpenSite={(c) => {
          setView('map');
          if (c.lat != null && c.lng != null) {
            setCenter([c.lat, c.lng]);
            if (c.code) setDetailCode({ code: c.code, lat: c.lat, lng: c.lng });
            else setPoint([c.lat, c.lng]);
          }
        }}/>
      ) : (
      <>
      {/* Search bar */}
      <div className="mb-3 relative">
        <div className="flex items-center gap-2 bg-black/40 border border-white/10 focus-within:border-[#A3FF12]/50 rounded-xl px-3 py-2">
          <Search size={16} className="text-white/40"/>
          <input
            value={searchQ}
            onChange={(e) => { setSearchQ(e.target.value); setSearchOpen(true); }}
            onFocus={() => setSearchOpen(true)}
            placeholder="Buscar por código de nodo (AND0001, MAD0331…) o torre física (SPA-…) o municipio"
            data-testid="site-search-input"
            className="flex-1 bg-transparent text-white text-sm placeholder:text-white/30 outline-none"
          />
          {searchLoading && <Loader2 size={14} className="animate-spin text-[#A3FF12]"/>}
          {searchQ && <button onClick={() => { setSearchQ(''); setSearchResults([]); }} className="text-white/40 hover:text-white"><X size={14}/></button>}
        </div>
        {searchOpen && searchResults.length > 0 && (
          <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden shadow-2xl max-h-80 overflow-y-auto" data-testid="site-search-results">
            {searchResults.map((r, i) => (
              <button key={`${r.kind}-${r.id}-${i}`} onClick={() => gotoResult(r)}
                className="w-full text-left px-3 py-2 hover:bg-[#A3FF12]/10 border-b border-white/5 last:border-0 flex items-start gap-2">
                <span className={`text-[10px] mt-0.5 font-bold px-1.5 py-0.5 rounded ${r.kind === 'tower' ? 'bg-purple-500/20 text-purple-300' : 'bg-[#A3FF12]/20 text-[#A3FF12]'}`}>
                  {r.kind === 'tower' ? 'TORRE' : 'NODO'}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-bold truncate">{r.label}</div>
                  <div className="text-white/40 text-xs truncate">{r.sub}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {showNodes && <OperatorFilter nodeStats={nodeStats} opFilter={opFilter} setOpFilter={setOpFilter} visibleCount={nodesFiltered.length}/>}

      {/* Status filter — Sin visitar / Activos / Conflictivos */}
      <div className="mb-4 flex flex-wrap items-center gap-2 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs" data-testid="status-filter-row">
        <span className="font-bold text-white/70 mr-1">Estado:</span>
        {[
          { key: null,           label: 'Todos',         cls: 'text-white',       ring: 'bg-white/10 border-white/40' },
          { key: 'sin_visitar',  label: 'Sin visitar',   cls: 'text-yellow-200',  ring: 'bg-yellow-500/15 border-yellow-500/40',
            dot: '#facc15', count: null },
          { key: 'activos',      label: 'Activos',       cls: 'text-[#A3FF12]',   ring: 'bg-[#A3FF12]/15 border-[#A3FF12]/40',
            dot: '#A3FF12', count: activityStatus.counts.active },
          { key: 'conflictivos', label: 'Conflictivos',  cls: 'text-red-300',     ring: 'bg-red-500/15 border-red-500/40',
            dot: '#ef4444', count: activityStatus.counts.conflict },
        ].map((s) => {
          const active = statusFilter === s.key;
          return (
            <button key={s.key || 'all'} onClick={() => setStatusFilter(s.key)}
              data-testid={`status-chip-${s.key || 'all'}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold border transition-colors ${
                active ? `${s.ring} ${s.cls}` : 'text-white/50 border-white/10 hover:text-white'
              }`}>
              {s.dot && <span style={{ background: s.dot, width: 8, height: 8, borderRadius: '50%' }}/>}
              {s.label}
              {s.count != null && s.count > 0 && (
                <span className="text-[10px] opacity-70">({s.count.toLocaleString('es-ES')})</span>
              )}
            </button>
          );
        })}
        {statusFilter && (
          <span className="text-[10px] text-white/40 ml-auto">
            {statusFilter === 'sin_visitar' ? 'Sites sin actividad registrada' :
             statusFilter === 'activos'     ? 'Con incidencias, negociaciones, agenda o preventivas en curso' :
                                              'En colección de conflictivos'}
            {' · '}
            {(showNodes ? nodesFiltered.length : 0) + (showTotemTowers ? towersFiltered.length : 0)} visibles
          </span>
        )}
      </div>

      {/* Hint when filter is on but no marker is visible in the current bbox */}
      {statusFilter && statusFilter !== 'sin_visitar' &&
        (showNodes ? nodesFiltered.length : 0) + (showTotemTowers ? towersFiltered.length : 0) === 0 &&
        ((statusFilter === 'activos'      ? activityStatus.counts.active   : activityStatus.counts.conflict) > 0) && (
        <div data-testid="status-hint-outside-bbox"
          className="mb-4 flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 text-xs rounded-xl px-3 py-2">
          <span>💡</span>
          Hay {statusFilter === 'activos' ? activityStatus.counts.active : activityStatus.counts.conflict}
          {' '}{statusFilter === 'activos' ? 'activos' : 'conflictivos'} fuera del área visible.
          Aleja el zoom o navega el mapa para verlos.
        </div>
      )}

      <form onSubmit={doSearch} className="flex gap-2 mb-5">
        <div className="flex-1 relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"/>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar dirección, ciudad o código postal..."
            className="w-full bg-[#0a0a0a] border border-white/10 text-white rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
        </div>
        <button type="submit" className="bg-white/5 border border-white/10 text-white px-5 py-3 rounded-xl font-bold hover:border-[#A3FF12]/40 transition-colors">Buscar</button>
      </form>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        {/* Map — se minimiza cuando hay resultados de prospección visibles */}
        <div className={`rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0a] relative transition-all ${
          analysis ? 'h-[28vh] lg:h-[32vh]' : 'h-[50vh] lg:h-[70vh]'
        }`}>
          {drawMode && (
            <div data-testid="draw-panel"
              className="absolute top-3 left-3 z-[1000] bg-black/85 backdrop-blur border border-[#A3FF12]/50 rounded-xl px-3 py-2.5 shadow-2xl flex items-center gap-2 flex-wrap max-w-[calc(100%-24px)]">
              <div className="flex items-center gap-2 text-[#A3FF12]">
                <Pencil size={14}/>
                <span className="text-xs font-black">
                  Dibuja el área ({drawPoints.length} vértices)
                </span>
              </div>
              <div className="text-white/50 text-[10px]">
                Clic para añadir · Doble clic para cerrar
              </div>
              <div className="ml-auto flex items-center gap-1.5">
                <button onClick={undoLastPoint} disabled={drawPoints.length === 0}
                  data-testid="btn-draw-undo"
                  className="flex items-center gap-1 bg-white/10 hover:bg-white/20 text-white text-[11px] font-bold px-2 py-1 rounded-lg disabled:opacity-30">
                  <Undo2 size={11}/> Deshacer
                </button>
                <button onClick={() => finishDraw()} disabled={drawPoints.length < 3}
                  data-testid="btn-draw-finish"
                  className="flex items-center gap-1 bg-[#A3FF12] hover:bg-[#b8ff3a] text-black text-[11px] font-black px-3 py-1 rounded-lg disabled:opacity-30">
                  <CheckCircle size={11}/> Cerrar y guardar
                </button>
                <button onClick={cancelDraw} data-testid="btn-draw-cancel"
                  className="text-white/60 hover:text-white text-[11px] font-bold px-2 py-1">
                  Cancelar
                </button>
              </div>
            </div>
          )}
          <MapContainer center={center} zoom={13} preferCanvas={true} style={{ width: '100%', height: '100%' }} doubleClickZoom={!drawMode}>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
            <FlyTo pos={center}/>
            {!drawMode && <ClickHandler onClick={onMapClick}/>}
            <AreaDrawer enabled={drawMode} points={drawPoints} onAddPoint={addDrawPoint} onFinish={() => finishDraw()}/>
            <NodesFetcher enabled={showNodes || showTotemTowers} onBoundsChange={fetchNodesInBbox}/>
            {showNodes && <NodesLayer nodes={nodesFiltered} onOpenDetail={setDetailCode}/>}
            {showTotemTowers && <TowersLayer towers={towersFiltered} onOpenDetail={setDetailCode}/>}
            {kmzAreas.map((k) => (
              <GeoJSON key={k.id} data={k.geojson} style={{ color: '#A3FF12', fillColor: '#A3FF12', fillOpacity: 0.1, weight: 2 }}/>
            ))}
            {coverage.map((z) => (
              <GeoJSON key={z.id} data={z.geojson} style={CoverageStyle(z.quality)}>
              </GeoJSON>
            ))}
            {point && (
              <Marker position={point}>
                <Popup>
                  <strong>{info?.address?.display_name?.split(',')[0] || 'Punto'}</strong>
                </Popup>
              </Marker>
            )}
            {towers.map((t) => (
              <Marker key={t.id} position={[t.lat, t.lng]} icon={towerIcon}>
                <Popup>
                  <div>
                    <strong>Torre {t.type || 'comm.'}</strong><br/>
                    {t.operator && <><span style={{color:'#888'}}>Operador:</span> {t.operator}<br/></>}
                    {t.height_m && <><span style={{color:'#888'}}>Altura:</span> {t.height_m}m</>}
                  </div>
                </Popup>
              </Marker>
            ))}
            {analysis?.protected?.filter((p) => p.lat && p.lng).map((p) => (
              <Marker key={`prot-${p.id}`} position={[p.lat, p.lng]} icon={protectedIcon}>
                <Popup>
                  <div>
                    <strong style={{color:'#a855f7'}}>🏛 {p.name}</strong><br/>
                    {p.kind && <><span style={{color:'#888'}}>Tipo:</span> {p.kind}<br/></>}
                    {p.ref_bic && <><span style={{color:'#888'}}>BIC:</span> {p.ref_bic}</>}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          {/* Click info */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="text-[#A3FF12]" size={18}/>
              <h3 className="text-white font-bold">Punto seleccionado</h3>
            </div>
            {!point && <p className="text-white/40 text-sm">Haz click en el mapa para ver dirección, altura media de edificios y normativa.</p>}
            {loadingInfo && <p className="text-white/50 text-sm">Cargando datos...</p>}
            {info && !loadingInfo && (
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-wide">Dirección</div>
                  <div className="text-white">{info.address?.display_name || 'No encontrada'}</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-black/40 border border-white/5 rounded-lg p-3">
                    <div className="text-white/40 text-xs">Altura media</div>
                    <div className="text-[#A3FF12] font-black text-lg">{info.height?.avg ? `${info.height.avg} m` : 'N/D'}</div>
                    {info.height?.count && <div className="text-white/40 text-xs">{info.height.count} edificios</div>}
                  </div>
                  <div className="bg-black/40 border border-white/5 rounded-lg p-3">
                    <div className="text-white/40 text-xs">Coordenadas</div>
                    <div className="text-white text-xs">{info.lat.toFixed(5)}<br/>{info.lng.toFixed(5)}</div>
                  </div>
                </div>
                <button onClick={saveAsSite} disabled={saving}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#A3FF12] text-black py-2.5 rounded-xl font-bold hover:bg-[#b8ff3a] transition-colors disabled:opacity-60">
                  <Save size={16}/> Guardar emplazamiento
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <a href={streetViewUrl(info.lat, info.lng)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 border border-white/15 text-white py-2 rounded-xl text-sm font-bold hover:bg-white/5 hover:border-white/30 transition-colors">
                    <Eye size={14}/> Street View
                  </a>
                  <a href={googleMapsUrl(info.lat, info.lng)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 border border-white/15 text-white py-2 rounded-xl text-sm font-bold hover:bg-white/5 hover:border-white/30 transition-colors">
                    <MapPin size={14}/> Mapa Google
                  </a>
                </div>
                <button onClick={researchNormativaAI} disabled={researching}
                  className="w-full inline-flex items-center justify-center gap-2 border border-[#A3FF12]/40 text-[#A3FF12] py-2.5 rounded-xl font-bold hover:bg-[#A3FF12]/10 transition-colors disabled:opacity-60 uppercase tracking-wide">
                  {researching ? <Loader2 className="animate-spin" size={16}/> : <Sparkles size={16}/>}
                  GENERAR DOSIER LEGAL
                </button>
              </div>
            )}
          </div>

          {/* Catastro */}
          {catastro && (
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <FileSearch className="text-[#A3FF12]" size={18}/>
                <h3 className="text-white font-bold">Catastro</h3>
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between gap-3"><span className="text-white/50">Ref. catastral</span><span className="text-white font-mono text-xs break-all text-right">{catastro.reference}</span></div>
                {catastro.use && <div className="flex justify-between"><span className="text-white/50">Uso</span><span className="text-white font-semibold">{catastro.use}</span></div>}
                {catastro.surface_m2 && <div className="flex justify-between"><span className="text-white/50">Superficie</span><span className="text-white font-semibold">{catastro.surface_m2} m²</span></div>}
                {catastro.year_built && <div className="flex justify-between"><span className="text-white/50">Año construc.</span><span className="text-white font-semibold">{catastro.year_built}</span></div>}
              </div>
            </div>
          )}

          {/* Catalogación del edificio (BIC / patrimonio / catálogo OSM) */}
          {heritage && (
            heritage.found ? (
              <div className={`rounded-2xl p-5 border ${
                heritage.severity === 'alta' ? 'bg-red-500/10 border-red-500/40' :
                heritage.severity === 'media' ? 'bg-purple-500/10 border-purple-500/40' :
                'bg-orange-500/10 border-orange-500/30'
              }`} data-testid="heritage-card">
                <div className="flex items-center gap-2 mb-3">
                  <Landmark className={
                    heritage.severity === 'alta' ? 'text-red-400' :
                    heritage.severity === 'media' ? 'text-purple-300' :
                    'text-orange-300'
                  } size={18}/>
                  <h3 className="text-white font-bold">Edificio catalogado</h3>
                  <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ml-auto ${
                    heritage.severity === 'alta' ? 'bg-red-500/30 text-red-200' :
                    heritage.severity === 'media' ? 'bg-purple-500/30 text-purple-200' :
                    'bg-orange-500/30 text-orange-200'
                  }`}>
                    Protección {heritage.severity || 'baja'}
                  </span>
                </div>
                {heritage.closest?.name && (
                  <div className="text-white font-black text-sm mb-1">{heritage.closest.name}</div>
                )}
                {heritage.summary && (
                  <div className="text-white/80 text-xs leading-relaxed">{heritage.summary}</div>
                )}
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                  {heritage.closest?.ref_bic && (
                    <div className="bg-black/30 rounded-lg px-2 py-1.5">
                      <div className="text-white/40 text-[10px] uppercase">BIC</div>
                      <div className="text-white font-bold">{heritage.closest.ref_bic}</div>
                    </div>
                  )}
                  {heritage.closest?.heritage && (
                    <div className="bg-black/30 rounded-lg px-2 py-1.5">
                      <div className="text-white/40 text-[10px] uppercase">Nivel heritage</div>
                      <div className="text-white font-bold">{heritage.closest.heritage}</div>
                    </div>
                  )}
                  {heritage.closest?.historic && (
                    <div className="bg-black/30 rounded-lg px-2 py-1.5">
                      <div className="text-white/40 text-[10px] uppercase">Tipo histórico</div>
                      <div className="text-white font-bold capitalize">{heritage.closest.historic}</div>
                    </div>
                  )}
                  {heritage.closest?.distance_m != null && (
                    <div className="bg-black/30 rounded-lg px-2 py-1.5">
                      <div className="text-white/40 text-[10px] uppercase">Distancia</div>
                      <div className="text-white font-bold">{heritage.closest.distance_m} m</div>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {heritage.closest?.wikidata && (
                    <a href={`https://www.wikidata.org/wiki/${heritage.closest.wikidata}`} target="_blank" rel="noreferrer"
                      className="text-white/60 hover:text-white text-[11px] font-bold inline-flex items-center gap-1 border border-white/20 rounded-full px-2 py-1">
                      Wikidata <ExternalLink size={10}/>
                    </a>
                  )}
                  {heritage.closest?.wikipedia && (
                    <a href={`https://es.wikipedia.org/wiki/${(heritage.closest.wikipedia || '').replace('es:', '')}`} target="_blank" rel="noreferrer"
                      className="text-white/60 hover:text-white text-[11px] font-bold inline-flex items-center gap-1 border border-white/20 rounded-full px-2 py-1">
                      Wikipedia <ExternalLink size={10}/>
                    </a>
                  )}
                </div>
                <div className="text-white/40 text-[10px] mt-3 pt-2 border-t border-white/10">
                  ⚠ Cualquier intervención requiere licencia patrimonial y aprobación de la comisión de patrimonio de la CCAA.
                </div>
                {heritage.wikidata && heritage.wikidata.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/10" data-testid="heritage-wikidata-list">
                    <div className="text-[10px] font-black uppercase tracking-widest text-[#A3FF12] mb-2">Registro oficial (Wikidata + BIC)</div>
                    <div className="space-y-1.5">
                      {heritage.wikidata.map((w) => (
                        <a key={w.qid} href={w.url} target="_blank" rel="noreferrer"
                          className="block bg-black/30 hover:bg-black/50 border border-white/10 hover:border-[#A3FF12]/40 rounded-lg px-2.5 py-2 transition-colors">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white text-xs font-bold flex-1 min-w-0 truncate">{w.name || w.qid}</span>
                            {w.distance_m != null && <span className="text-white/40 text-[10px] flex-shrink-0">{w.distance_m} m</span>}
                            <ExternalLink size={11} className="text-white/40 flex-shrink-0"/>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap mt-0.5">
                            {w.protection && (
                              <span className="text-[9px] font-black uppercase tracking-widest text-red-300 bg-red-500/20 px-1.5 py-0.5 rounded-full">
                                {w.protection}
                              </span>
                            )}
                            {w.bic_code && <span className="text-[10px] text-white/60 font-mono">BIC {w.bic_code}</span>}
                            {w.declared_date && <span className="text-[10px] text-white/50">declarado {w.declared_date}</span>}
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-[#A3FF12]/5 border border-[#A3FF12]/20 rounded-2xl p-4" data-testid="heritage-card-clean">
                <div className="flex items-center gap-2">
                  <Landmark className="text-[#A3FF12]" size={16}/>
                  <div className="text-white/80 text-xs">
                    <b className="text-[#A3FF12]">Sin catalogación patrimonial</b> registrada en un radio de {heritage.radius_m || 25} m.
                  </div>
                </div>
              </div>
            )
          )}

          {/* Coverage (towers) */}
          {coverageInfo && (
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Radio className="text-[#A3FF12]" size={18}/>
                <h3 className="text-white font-bold">Cobertura próxima</h3>
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-white/50">Torres en 2 km</span><span className="text-white font-black">{coverageInfo.count}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Calidad estimada</span>
                  <span className={`font-bold uppercase ${coverageInfo.quality === 'alta' ? 'text-[#A3FF12]' : coverageInfo.quality === 'media' ? 'text-orange-400' : 'text-red-400'}`}>
                    {coverageInfo.quality}
                  </span>
                </div>
                {coverageInfo.quality === 'baja' && <div className="text-red-400 text-xs mt-2">⚡ Zona con oportunidad de oferta</div>}
                <div className="text-white/40 text-xs pt-2 border-t border-white/5 mt-2">Fuente: OpenStreetMap (torres registradas)</div>
              </div>
            </div>
          )}

          {/* Electricity infra (especially for rural) */}
          {electricity && (
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Zap className="text-yellow-400" size={18}/>
                <h3 className="text-white font-bold">Suministro eléctrico</h3>
                {electricity.has_electricity ? (
                  <span className="text-[10px] font-black bg-[#A3FF12]/15 text-[#A3FF12] border border-[#A3FF12]/30 px-2 py-0.5 rounded-full">CON LUZ</span>
                ) : (
                  <span className="text-[10px] font-black bg-red-500/15 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-full">SIN INFRA. CERCANA</span>
                )}
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex gap-2 flex-wrap">
                  {electricity.has_low_voltage && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30">BAJA TENSIÓN</span>}
                  {electricity.has_medium_voltage && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/30">MEDIA TENSIÓN</span>}
                  {electricity.has_high_voltage && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30">ALTA TENSIÓN</span>}
                </div>
                {electricity.voltages_kV?.length > 0 && (
                  <div className="flex justify-between"><span className="text-white/50">Voltajes detectados</span><span className="text-white font-semibold text-xs">{electricity.voltages_kV.map((v) => `${v} kV`).join(' · ')}</span></div>
                )}
                <div className="flex justify-between"><span className="text-white/50">Transformador más cerca</span><span className={`font-semibold ${electricity.nearest_transformer_m && electricity.nearest_transformer_m < 500 ? 'text-[#A3FF12]' : 'text-white'}`}>{electricity.nearest_transformer_m != null ? `${electricity.nearest_transformer_m} m` : 'N/D'}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Línea más cerca</span><span className={`font-semibold ${electricity.nearest_line_m && electricity.nearest_line_m < 500 ? 'text-[#A3FF12]' : 'text-white'}`}>{electricity.nearest_line_m != null ? `${electricity.nearest_line_m} m` : 'N/D'}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Subestación más cerca</span><span className="text-white font-semibold">{electricity.nearest_substation_m != null ? `${electricity.nearest_substation_m} m` : 'N/D'}</span></div>
                <div className="flex justify-between text-xs pt-2 border-t border-white/5 mt-2 text-white/40">
                  <span>{electricity.counts?.lines ?? 0} líneas · {electricity.counts?.transformers ?? 0} trafos · {electricity.counts?.substations ?? 0} subest. · {electricity.counts?.poles ?? 0} postes</span>
                </div>
              </div>
            </div>
          )}

          {/* AI Normativa */}
          {aiNorm && (
            <div className="bg-[#0a0a0a] border border-[#A3FF12]/40 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Sparkles className="text-[#A3FF12]" size={18}/>
                <h3 className="text-white font-bold">Dossier legal · {aiNorm.municipality}</h3>
                {aiNorm.has_local_ordinance === false && <span className="text-[10px] font-black tracking-widest bg-orange-500/15 text-orange-400 px-2 py-0.5 rounded-full border border-orange-500/30">SIN ORDENANZA</span>}
                {aiNorm.has_local_ordinance === true && <span className="text-[10px] font-black tracking-widest bg-[#A3FF12]/15 text-[#A3FF12] px-2 py-0.5 rounded-full border border-[#A3FF12]/30">CON ORDENANZA</span>}
              </div>
              {/* Afecciones legales (10 tipos) — PRIMERO en el dosier */}
              {aiNorm.afecciones && aiNorm.afecciones.length > 0 ? (
                <div className="mb-4 pb-4 border-b border-white/10" data-testid="dossier-afecciones">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert className="text-[#A3FF12]" size={16}/>
                    <h4 className="text-white font-black text-sm">Afecciones legales (10)</h4>
                  </div>
                  {aiNorm.afecciones_summary && (
                    <div className="bg-[#A3FF12]/5 border border-[#A3FF12]/30 text-white/80 rounded-xl px-3 py-2 text-xs mb-2 leading-relaxed">
                      <b className="text-[#A3FF12]">Resumen:</b> {aiNorm.afecciones_summary}
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {aiNorm.afecciones.map((a) => {
                      const map = {
                        compatible:   { label: 'Compatible',   color: 'text-[#A3FF12]', bg: 'bg-[#A3FF12]/10 border-[#A3FF12]/30' },
                        posible:      { label: 'Posible',      color: 'text-yellow-300', bg: 'bg-yellow-500/10 border-yellow-500/30' },
                        afectado:     { label: 'Afectado',     color: 'text-orange-300', bg: 'bg-orange-500/10 border-orange-500/30' },
                        incompatible: { label: 'Incompatible', color: 'text-red-300',    bg: 'bg-red-500/10 border-red-500/30' },
                        sin_datos:    { label: 'Sin datos',    color: 'text-white/40',   bg: 'bg-white/5 border-white/10' },
                      };
                      const meta = map[a.status] || map.sin_datos;
                      return (
                        <div key={a.id} className={`rounded-lg border px-2.5 py-1.5 ${meta.bg}`} data-testid={`dossier-af-${a.id}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white text-xs font-bold flex-1 min-w-0">{a.name}</span>
                            <span className={`text-[9px] font-black uppercase tracking-widest ${meta.color} bg-black/30 px-1.5 py-0.5 rounded-full flex-shrink-0`}>
                              {meta.label}
                            </span>
                            {a.url && (
                              <a href={a.url} target="_blank" rel="noreferrer" className="text-white/40 hover:text-[#A3FF12] flex-shrink-0" title="Consulta oficial">
                                <ExternalLink size={11}/>
                              </a>
                            )}
                          </div>
                          {a.details && <div className="text-white/60 text-[11px] mt-0.5 leading-snug">{a.details}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="mb-4 pb-4 border-b border-white/10" data-testid="dossier-afecciones-loading">
                  {aiNorm.afecciones_error ? (
                    <div className="flex items-center gap-2 text-white/40 text-xs">
                      <ShieldAlert size={14} className="text-white/40"/>
                      No se pudieron cargar las afecciones legales. Vuelve a intentarlo desde «Análisis de zona».
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-white/50 text-xs">
                      <Loader2 className="animate-spin" size={14}/>
                      <ShieldAlert size={14} className="text-[#A3FF12]"/>
                      Analizando 10 afecciones legales sobre la coordenada…
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-1.5 text-sm">
                {aiNorm.max_height_m != null && <div className="flex justify-between"><span className="text-white/50">Altura máx</span><span className="text-white font-semibold">{aiNorm.max_height_m} m</span></div>}
                {aiNorm.min_distance_residential_m != null && <div className="flex justify-between"><span className="text-white/50">Dist. residencial</span><span className="text-white font-semibold">{aiNorm.min_distance_residential_m} m</span></div>}
                {aiNorm.retranqueo_m != null && <div className="flex justify-between"><span className="text-white/50">Retranqueo</span><span className="text-white font-semibold">{aiNorm.retranqueo_m} m</span></div>}
                {aiNorm.license_regime && (
                  <div className="flex justify-between items-center">
                    <span className="text-white/50">Régimen</span>
                    <span className={`text-xs font-black uppercase px-2 py-1 rounded-full border ${
                      aiNorm.license_regime === 'licencia' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                      aiNorm.license_regime === 'declaracion_responsable' ? 'bg-[#A3FF12]/10 text-[#A3FF12] border-[#A3FF12]/30' :
                      aiNorm.license_regime === 'comunicacion_previa' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' :
                      'bg-orange-500/10 text-orange-400 border-orange-500/30'
                    }`}>
                      {aiNorm.license_regime === 'declaracion_responsable' ? 'DR' :
                       aiNorm.license_regime === 'comunicacion_previa' ? 'COM. PREVIA' :
                       aiNorm.license_regime.toUpperCase()}
                    </span>
                  </div>
                )}
                {aiNorm.license_conditions && (
                  <div className="bg-blue-500/5 border border-blue-500/20 text-blue-100 text-xs rounded-lg px-3 py-2 mt-2 leading-relaxed">
                    <strong className="text-blue-300">📋 Condiciones régimen:</strong> {aiNorm.license_conditions}
                  </div>
                )}
                {aiNorm.extra_rules && (
                  <div className="bg-orange-500/10 border border-orange-500/20 text-orange-200 text-xs rounded-lg px-3 py-2 mt-2 leading-relaxed">
                    <strong>⚠ Reglas extra:</strong> {aiNorm.extra_rules}
                  </div>
                )}
                {aiNorm.protected_zones && (
                  <div className="bg-purple-500/10 border border-purple-500/20 text-purple-200 text-xs rounded-lg px-3 py-2 mt-2 leading-relaxed">
                    <strong>🏛 Zonas protegidas:</strong> {aiNorm.protected_zones}
                  </div>
                )}
                {aiNorm.pgou_reference && (
                  <div className="text-white/70 text-xs mt-2 pt-2 border-t border-white/5 leading-relaxed">
                    <strong className="text-[#A3FF12]">PGOU:</strong> {aiNorm.pgou_reference}
                    {aiNorm.pgou_url && (
                      <a href={aiNorm.pgou_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#A3FF12] ml-1 hover:underline">
                        (link) <ExternalLink size={10}/>
                      </a>
                    )}
                  </div>
                )}
                {aiNorm.state_framework && <div className="text-white/60 text-xs leading-relaxed"><strong className="text-white/80">Marco estatal:</strong> {aiNorm.state_framework}</div>}
                {aiNorm.autonomic_framework && <div className="text-white/60 text-xs leading-relaxed"><strong className="text-white/80">Marco autonómico:</strong> {aiNorm.autonomic_framework}</div>}
                {aiNorm.notes && <p className="text-white/70 text-xs mt-2 pt-2 border-t border-white/5 leading-relaxed">{aiNorm.notes}</p>}
                {aiNorm.source_url && (
                  <a href={aiNorm.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#A3FF12] text-xs font-bold hover:underline mt-2">
                    Ordenanza municipal <ExternalLink size={12}/>
                  </a>
                )}
              </div>
              <button onClick={saveAiNormToDb} className="w-full mt-4 bg-[#A3FF12] text-black py-2 rounded-xl text-sm font-bold hover:bg-[#b8ff3a]">
                Guardar en BBDD
              </button>
            </div>
          )}

          {/* Normativa */}
          {matchedNorm && (
            <div className="bg-[#0a0a0a] border border-[#A3FF12]/30 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Building className="text-[#A3FF12]" size={18}/>
                <h3 className="text-white font-bold">Normativa {matchedNorm.municipality}</h3>
                {matchedNorm.verified && <span className="text-[10px] font-black tracking-widest bg-[#A3FF12]/20 text-[#A3FF12] px-2 py-0.5 rounded-full">VERIFICADA</span>}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-white/50">Tipo</span><span className="text-white font-semibold uppercase">{matchedNorm.type}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Altura máx</span><span className="text-white font-semibold">{matchedNorm.max_height_m ? `${matchedNorm.max_height_m} m` : 'N/D'}</span></div>
                {matchedNorm.min_distance_residential_m != null && <div className="flex justify-between"><span className="text-white/50">Dist. mín residencial</span><span className="text-white font-semibold">{matchedNorm.min_distance_residential_m} m</span></div>}
                {matchedNorm.retranqueo_m != null && <div className="flex justify-between"><span className="text-white/50">Retranqueo</span><span className="text-white font-semibold">{matchedNorm.retranqueo_m} m</span></div>}
                <div className="flex justify-between"><span className="text-white/50">Licencia</span><span className="text-white font-semibold">{matchedNorm.requires_license ? 'Sí' : 'No'}</span></div>
                {matchedNorm.extra_rules && (
                  <div className="bg-orange-500/10 border border-orange-500/20 text-orange-200 text-xs rounded-lg px-3 py-2 mt-2">
                    <strong>⚠ Reglas extra:</strong> {matchedNorm.extra_rules}
                  </div>
                )}
                {matchedNorm.notes && <div className="text-white/60 text-xs mt-3 pt-3 border-t border-white/5 leading-relaxed">{matchedNorm.notes}</div>}
                {matchedNorm.source_url && (
                  <a href={matchedNorm.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#A3FF12] text-xs font-bold hover:underline mt-2">
                    Ver fuente <ExternalLink size={12}/>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Analysis types selector */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4" data-testid="analysis-types-panel">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="text-[#A3FF12]" size={16}/>
              <h3 className="text-white font-bold text-sm">Qué prospectar</h3>
            </div>
            <p className="text-white/40 text-[10px] mb-2 leading-tight">
              Elige categorías antes de "Prospectar en el área". Cuantas menos, más rápido y limpio.
            </p>
            <div className="grid grid-cols-2 gap-1">
              {[
                { id: 'buildings', label: 'Edificios altos', desc: '≥3 plantas, oficinas, apart.' },
                { id: 'towers',    label: 'Torres / mástiles', desc: 'Antenas, silos, chimeneas' },
                { id: 'shops',     label: 'Tiendas / locales', desc: 'Comercio, mercados' },
                { id: 'hotels',    label: 'Hoteles / turismo', desc: 'Hostal, hotel, resort' },
                { id: 'organisms', label: 'Organismos públicos', desc: 'Ayto., policía, bomberos' },
                { id: 'education', label: 'Educación',   desc: 'Colegios, univ., biblioteca' },
                { id: 'health',    label: 'Sanidad',     desc: 'Hospital, clínica, farmacia' },
                { id: 'religion',  label: 'Iglesias / culto', desc: 'Iglesia, mezquita, capilla' },
                { id: 'heritage',  label: 'Patrimonio / BIC', desc: 'Monumentos protegidos' },
                { id: 'sports',    label: 'Deporte',     desc: 'Estadio, piscina, pabellón' },
              ].map((t) => {
                const active = !!analysisTypes[t.id];
                return (
                  <label key={t.id}
                    data-testid={`chk-type-${t.id}`}
                    className={`flex items-start gap-1.5 rounded-lg px-2 py-1.5 cursor-pointer border transition-colors ${
                      active ? 'bg-[#A3FF12]/10 border-[#A3FF12]/40' : 'bg-white/[0.02] border-white/10 hover:border-white/25'
                    }`}>
                    <input type="checkbox" checked={active}
                      onChange={(e) => setAnalysisTypes((prev) => ({ ...prev, [t.id]: e.target.checked }))}
                      className="mt-0.5 w-3 h-3 accent-[#A3FF12] flex-shrink-0"/>
                    <div className="min-w-0">
                      <div className={`text-[11px] font-black leading-tight ${active ? 'text-white' : 'text-white/70'}`}>{t.label}</div>
                      <div className="text-[9px] text-white/40 leading-tight truncate">{t.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2 text-[10px]">
              <button onClick={() => setAnalysisTypes(Object.fromEntries(Object.keys(analysisTypes).map((k) => [k, true])))}
                className="text-[#A3FF12] hover:underline font-bold">Todo</button>
              <button onClick={() => setAnalysisTypes(Object.fromEntries(Object.keys(analysisTypes).map((k) => [k, false])))}
                className="text-white/50 hover:text-white/80 font-bold">Ninguno</button>
              <button onClick={() => setAnalysisTypes({ buildings: true, towers: true, heritage: true, shops: false, hotels: false, organisms: false, education: false, health: false, religion: false, sports: false })}
                className="text-white/50 hover:text-white/80 font-bold ml-auto">Por defecto</button>
            </div>
          </div>

          {/* KMZ layers */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Layers className="text-[#A3FF12]" size={18}/>
                <h3 className="text-white font-bold">Capas KMZ ({kmzAreas.length})</h3>
              </div>
              <button onClick={() => fileRef.current?.click()} data-testid="btn-panel-import-kmz"
                className="text-[#A3FF12] hover:bg-[#A3FF12]/10 border border-[#A3FF12]/40 text-[10px] font-black px-2 py-1 rounded-full flex items-center gap-1">
                <Upload size={10}/> IMPORTAR
              </button>
            </div>
            {kmzAreas.length === 0 ? (
              <div className="text-center py-4 border border-dashed border-white/10 rounded-lg">
                <p className="text-white/40 text-sm mb-1">Sin áreas de búsqueda</p>
                <p className="text-white/30 text-xs">Sube un KMZ con polígonos para prospectar dentro</p>
              </div>
            ) : (
              <div className="space-y-2">
                {kmzAreas.map((k) => {
                  const feats = k.geojson?.features || [];
                  const polys = feats.filter((f) => f.geometry?.type === 'Polygon').length;
                  const points = feats.filter((f) => f.geometry?.type === 'Point').length;
                  const lines = feats.filter((f) => f.geometry?.type === 'LineString').length;
                  return (
                    <div key={k.id} data-testid={`kmz-area-${k.id}`}
                      className="bg-black/40 border border-white/10 rounded-lg p-2.5">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-white text-sm truncate flex-1 font-bold">{k.name}</span>
                        <button onClick={() => deleteKmz(k.id)} data-testid={`btn-delete-kmz-${k.id}`}
                          className="text-white/40 hover:text-red-400 p-1 flex-shrink-0"><Trash2 size={12}/></button>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mb-2">
                        {polys > 0 && (
                          <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-[#A3FF12]/15 text-[#A3FF12] border border-[#A3FF12]/30">
                            {polys} área{polys !== 1 ? 's' : ''}
                          </span>
                        )}
                        {points > 0 && (
                          <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30">
                            {points} punto{points !== 1 ? 's' : ''}
                          </span>
                        )}
                        {lines > 0 && (
                          <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-300 border border-orange-500/30">
                            {lines} línea{lines !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {polys > 0 ? (
                        <button onClick={() => reanalyze(k)} data-testid={`btn-prospect-kmz-${k.id}`}
                          className="w-full bg-[#A3FF12] hover:bg-[#b8ff3a] text-black text-xs font-black py-1.5 rounded-lg flex items-center justify-center gap-1.5">
                          <BarChart3 size={12}/> Prospectar en el área
                        </button>
                      ) : (
                        <div className="text-[10px] text-white/40 italic text-center">Sin polígonos analizables</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Coverage legend */}
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5">
            <h3 className="text-white font-bold mb-3 text-sm">Cobertura</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-red-500/40"/><span className="text-white/70">Baja — oportunidad</span></div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-orange-500/40"/><span className="text-white/70">Media</span></div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-[#A3FF12]/40"/><span className="text-white/70">Alta</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Analyzing overlay */}
      {analyzing && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center pointer-events-none">
          <div className="bg-[#0a0a0a] border border-[#A3FF12]/40 rounded-2xl p-6 flex items-center gap-3 shadow-2xl">
            <Loader2 className="animate-spin text-[#A3FF12]" size={24}/>
            <div>
              <div className="text-white font-bold">Analizando zona...</div>
              <div className="text-white/50 text-xs">Buscando edificios, calles, negocios y antenas en OSM</div>
            </div>
          </div>
        </div>
      )}

      <AreaAnalysisPanel open={!!analysis} data={analysis} areaName={analysisAreaName}
        onClose={() => setAnalysis(null)} onSaveSite={saveCandidate}/>
      </>
      )}
      {detailCode && <SiteMasterModal code={detailCode.code} lat={detailCode.lat} lng={detailCode.lng} onClose={() => setDetailCode(null)}/>}
    </div>
  );
};

export default Prospeccion;
